/**
 * Agent hotel HR pricing — single pipeline: normalize → resolve → format.
 * No FX; USD/ILS only via known hotel currency codes.
 *
 * @typedef {object} ResolvedAgentHotelPricingVm
 * @property {number|null} basePrice
 * @property {string|null} baseCurrency
 * @property {number} commissionPercent
 * @property {number|null} commissionAmount
 * @property {number|null} finalPrice
 * @property {string|null} displayCurrency
 * @property {string} pricingSource
 * @property {'ok'|'invalid_api_price'|'invalid_missing_currency'|'currency_triple_mismatch'|'entitlements_pending'} pricingStatus
 * @property {string|null} reason
 */

import {
  formatHotelSearchAmountForDisplay,
  isKnownHotelSearchCurrencyCode,
} from "../../constants/hotelCurrencies";
import { compactPricingAuditMeta } from "../../pricing/compactPricingAuditMeta.js";
import {
  PRICING_AUDIT_PRODUCT_TYPE,
  normalizePricingAuditProductType,
} from "../../pricing/pricingAuditProductType.js";
import { recordPricingAudit } from "../../pricing/pricingEngineAuditStore.js";
import {
  applyFXSettlement,
  roundMoney2,
  runAgentHotelHrMarkup,
} from "../../pricing/pricingEngine";
import {
  buildEntitlementsPendingHotelPricingVm,
  ENTITLEMENTS_FAILED_FALLBACK_ZERO,
  isHotelSearchEntitlementsFailed,
  isHotelSearchEntitlementsPending,
} from "./agentHotelEntitlementsPricingGate";

/** @param {string|null|undefined} raw */
function normKnownHotelCurrency(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const c = String(raw).trim().toUpperCase();
  return isKnownHotelSearchCurrencyCode(c) ? c : null;
}

/** @param {number} n */
export function roundHotelSearchMoney2(n) {
  return roundMoney2(n);
}

/**
 * @param {unknown} raw — entitlement value; invalid / missing → 0
 * @returns {number}
 */
export function coalesceHotelHrCommissionPercent(raw) {
  if (raw == null || raw === "") return 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return n;
}

/**
 * @param {unknown} raw
 * @returns {number}
 */
export function coalesceFixedHotelCommission(raw) {
  if (raw == null || raw === "") return 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return roundMoney2(n);
}

/**
 * FX helpers for agent hotel HR clamp (USD display → ILS supplier / commission).
 * @param {{ USD?: unknown }|null|undefined} currencyRate
 */
export function buildHotelAgentFxFromCurrencyRate(currencyRate) {
  const rate = Number(currencyRate?.USD);
  if (!Number.isFinite(rate) || rate <= 0) {
    return { usdToIls: null, ilsToUsd: null };
  }
  return {
    usdToIls: (usd, fixed = false) => {
      if (!Number.isFinite(Number(usd))) return 0;
      const value = Number(usd) * rate;
      return fixed ? Math.ceil(value * 100) / 100 : value;
    },
    ilsToUsd: (ils, fixed = false) => {
      if (!Number.isFinite(Number(ils))) return 0;
      const value = Number(ils) / rate;
      return fixed ? Math.ceil(value * 100) / 100 : value;
    },
  };
}

/**
 * Supplier net in ILS for HR clamp — explicit override, ILS flow, or USD→ILS via FX.
 */
export function resolveAgentHotelSupplierPriceIls({
  basePrice = null,
  flowCurrency = null,
  supplierPriceIls = null,
  usdToIls = null,
  ilsToUsd = null,
} = {}) {
  if (
    supplierPriceIls != null &&
    Number.isFinite(Number(supplierPriceIls)) &&
    Number(supplierPriceIls) > 0
  ) {
    return Number(supplierPriceIls);
  }
  if (basePrice == null || !Number.isFinite(Number(basePrice)) || Number(basePrice) <= 0) {
    return null;
  }
  const flow = String(flowCurrency ?? "ILS")
    .trim()
    .toUpperCase();
  if (flow === "ILS") return Number(basePrice);
  if (flow === "USD") {
    const fx = applyFXSettlement({
      amount: Number(basePrice),
      fromCurrency: "USD",
      toCurrency: "ILS",
      usdToIls,
      ilsToUsd,
    });
    return fx?.convertedAmount != null && Number.isFinite(Number(fx.convertedAmount))
      ? Number(fx.convertedAmount)
      : null;
  }
  return null;
}

function resolvePipelineFx(rawData = {}) {
  if (typeof rawData.usdToIls === "function") {
    return { usdToIls: rawData.usdToIls, ilsToUsd: rawData.ilsToUsd ?? null };
  }
  return buildHotelAgentFxFromCurrencyRate(rawData.currencyRate);
}

/**
 * @param {object} params
 * @param {string} [params.sourceType='agent_hotel_flow']
 * @param {object} params.rawData
 * @param {number|null|undefined} params.rawData.apiPrice
 * @param {string|null|undefined} params.rawData.transactionCurrency
 * @param {string|null|undefined} params.rawData.hotelSearchResultsCurrency
 * @param {string|null|undefined} params.rawData.hotelSearchListDisplayCurrency
 * @param {unknown} [params.rawData.hotelHrCommissionPercentage]
 * @param {unknown} [params.rawData.fixedHotelCommission]
 * @param {number|null|undefined} [params.rawData.supplierPriceIls]
 * @param {(n: number, fixed?: boolean) => number|null|undefined} [params.rawData.usdToIls]
 * @param {(n: number, fixed?: boolean) => number|null|undefined} [params.rawData.ilsToUsd]
 * @param {{ USD?: unknown }|null|undefined} [params.rawData.currencyRate]
 * @returns {{
 *   basePrice: number|null,
 *   baseCurrency: string|null,
 *   commissionPercent: number,
 *   pricingSource: string,
 *   resultsCurrency: string|null,
 *   displayCurrency: string|null,
 *   transactionCurrency: string|null,
 *   supplierPriceIls: number|null,
 *   usdToIls: ((n: number, fixed?: boolean) => number|null|undefined)|null,
 *   ilsToUsd: ((n: number, fixed?: boolean) => number|null|undefined)|null,
 * }}
 */
export function normalizeAgentHotelPricingInput({ sourceType = "agent_hotel_flow", rawData }) {
  const {
    apiPrice,
    transactionCurrency,
    hotelSearchResultsCurrency,
    hotelSearchListDisplayCurrency,
    hotelHrCommissionPercentage,
    fixedHotelCommission,
    supplierPriceIls: supplierPriceIlsRaw,
    usdToIls: usdToIlsRaw,
    ilsToUsd: ilsToUsdRaw,
    currencyRate,
  } = rawData || {};

  const fx = resolvePipelineFx(rawData || {});
  const usdToIls = typeof usdToIlsRaw === "function" ? usdToIlsRaw : fx.usdToIls;
  const ilsToUsd = typeof ilsToUsdRaw === "function" ? ilsToUsdRaw : fx.ilsToUsd;

  const txn = normKnownHotelCurrency(transactionCurrency);
  const res = normKnownHotelCurrency(hotelSearchResultsCurrency);
  const disp = normKnownHotelCurrency(hotelSearchListDisplayCurrency);
  const apiNum = Number(apiPrice);
  const basePrice =
    Number.isFinite(apiNum) && apiNum > 0 ? apiNum : null;

  /** ב-agent_hotel_flow מחיר החבילה לעיתים ללא מטבע בשורה — משתמשים ב-results/display מהחיפוש */
  const baseCurrency =
    sourceType === "agent_hotel_flow"
      ? txn ?? res ?? disp ?? null
      : txn ?? null;

  const flowCurrency = res ?? disp ?? txn ?? null;
  const supplierPriceIlsExplicit =
    supplierPriceIlsRaw != null &&
    Number.isFinite(Number(supplierPriceIlsRaw)) &&
    Number(supplierPriceIlsRaw) > 0
      ? Number(supplierPriceIlsRaw)
      : null;
  const supplierPriceIls =
    supplierPriceIlsExplicit ??
    resolveAgentHotelSupplierPriceIls({
      basePrice,
      flowCurrency,
      usdToIls,
      ilsToUsd,
    });

  return {
    basePrice,
    baseCurrency,
    commissionPercent: coalesceHotelHrCommissionPercent(hotelHrCommissionPercentage),
    fixedHotelCommission: coalesceFixedHotelCommission(fixedHotelCommission),
    pricingSource: sourceType,
    resultsCurrency: res,
    displayCurrency: disp,
    transactionCurrency: txn,
    supplierPriceIls,
    usdToIls,
    ilsToUsd,
  };
}

/**
 * @param {object} normalized — output of normalizeAgentHotelPricingInput
 * @param {string|null|undefined} [targetCurrency] — reserved for future FX paths; unused in HR hotel flow
 * @returns {{
 *   basePrice: number|null,
 *   baseCurrency: string|null,
 *   commissionPercent: number,
 *   commissionAmount: number|null,
 *   finalPrice: number|null,
 *   displayCurrency: string|null,
 *   pricingSource: string,
 *   pricingStatus: string,
 *   reason: string|null,
 * }}
 */
export function resolveAgentHotelPricing(normalized, targetCurrency = null) {
  void targetCurrency;
  const {
    basePrice,
    commissionPercent,
    pricingSource,
    resultsCurrency,
    displayCurrency,
    transactionCurrency,
    fixedHotelCommission,
    supplierPriceIls,
    usdToIls,
    ilsToUsd,
  } = normalized;

  const txn = transactionCurrency;
  const res = resultsCurrency;
  const disp = displayCurrency;
  const isAgentHotelFlow = pricingSource === "agent_hotel_flow";
  /**
   * HR agent hotel: אם אין מטבע עסקה בשורת חבילה, מספיק יישור results↔display (מטבע סט תוצאות ורשימה).
   * אם יש transactionCurrency — חובה שלישייה מלאה (כמו קודם; מונע מחיר שגוי כשהשורה ב-USD והחיפוש ב-ILS).
   */
  const tripleAligned = isAgentHotelFlow
    ? txn
      ? Boolean(txn && res && disp && txn === res && res === disp)
      : Boolean(res && disp && res === disp)
    : Boolean(txn && res && disp && txn === res && res === disp);

  if (basePrice == null) {
    return {
      basePrice: null,
      baseCurrency: txn ?? res ?? disp ?? null,
      commissionPercent,
      commissionAmount: null,
      finalPrice: null,
      displayCurrency: txn ?? res ?? disp ?? null,
      pricingSource,
      pricingStatus: "invalid_api_price",
      reason: "invalid_api_price",
      auditSteps: ["agent_hotel:reject:invalid_api_price"],
    };
  }

  if (!tripleAligned) {
    let reason = "currency_triple_mismatch";
    if (!res) reason = "no_results_currency";
    else if (!disp) reason = "no_display_currency";
    else if (!isAgentHotelFlow && !txn) reason = "no_transaction_currency";
    else if (txn && res && disp && (txn !== res || res !== disp)) {
      reason = "currency_triple_mismatch";
    } else if (isAgentHotelFlow && !txn && res && disp && res !== disp) {
      reason = "currency_triple_mismatch";
    }

    const missingAny = isAgentHotelFlow
      ? !res || !disp
      : !res || !disp || !txn;
    const pricingStatus = missingAny
      ? "invalid_missing_currency"
      : "currency_triple_mismatch";

    return {
      basePrice,
      baseCurrency: txn ?? res ?? disp ?? null,
      commissionPercent,
      commissionAmount: null,
      finalPrice: null,
      displayCurrency: txn ?? res ?? disp ?? null,
      pricingSource,
      pricingStatus,
      reason,
      auditSteps: [`agent_hotel:reject:${pricingStatus}:${reason}`],
    };
  }

  const pct = commissionPercent;
  const flowCurrency = res;
  const hrVm = runAgentHotelHrMarkup({
    basePrice,
    baseCurrency: flowCurrency,
    displayCurrency: flowCurrency,
    hotelHrCommissionPercent: pct,
    supplierPriceIls,
    usdToIls,
    ilsToUsd,
  });

  if (
    pct > 0 &&
    (hrVm.finalPrice == null ||
      hrVm.pricingStatus === "missing_supplier_ils_for_hr" ||
      hrVm.pricingStatus === "missing_fx_for_hr_commission")
  ) {
    return {
      basePrice,
      baseCurrency: flowCurrency,
      commissionPercent: pct,
      fixedHotelCommission: fixedHotelCommission || 0,
      commissionAmount: null,
      commissionIls: hrVm.commissionIls ?? null,
      finalPrice: null,
      displayCurrency: flowCurrency,
      pricingSource,
      pricingStatus: hrVm.pricingStatus ?? "missing_supplier_ils_for_hr",
      reason: hrVm.pricingStatus ?? "missing_supplier_ils_for_hr",
      auditSteps: [`agent_hotel:reject:${hrVm.pricingStatus ?? "missing_supplier_ils_for_hr"}`],
    };
  }

  const commissionAmount =
    (pct > 0 ? hrVm.commissionAmount ?? 0 : 0) + (fixedHotelCommission || 0);
  const finalPrice = roundMoney2((hrVm.finalPrice || 0) + (fixedHotelCommission || 0));

  return {
    basePrice,
    baseCurrency: flowCurrency,
    commissionPercent: pct,
    fixedHotelCommission: fixedHotelCommission || 0,
    commissionAmount,
    commissionIls: hrVm.commissionIls ?? null,
    finalPrice,
    displayCurrency: flowCurrency,
    pricingSource,
    pricingStatus: "ok",
    reason: null,
    auditSteps: [
      "agent_hotel:triple_ok",
      "agent_hotel:engine:runAgentHotelHrMarkup",
    ],
  };
}

/**
 * @param {string} [sourceType]
 * @param {object} rawData — same shape as normalize rawData + optional fields for search row
 */
export function runAgentHotelPricingPipeline({ sourceType = "agent_hotel_flow", rawData }) {
  const safeRaw =
    rawData && typeof rawData === "object" ? { ...rawData } : {};
  const pricingAuditMeta =
    safeRaw.pricingAuditMeta && typeof safeRaw.pricingAuditMeta === "object"
      ? safeRaw.pricingAuditMeta
      : null;
  const entitlementsStatus = safeRaw.entitlementsStatus;
  delete safeRaw.pricingAuditMeta;
  delete safeRaw.entitlementsStatus;

  const normalized = normalizeAgentHotelPricingInput({
    sourceType,
    rawData: safeRaw,
  });

  if (isHotelSearchEntitlementsPending(entitlementsStatus)) {
    const pending = buildEntitlementsPendingHotelPricingVm(normalized);
    recordPricingAudit({
      kind: "hotel",
      meta: compactPricingAuditMeta({
        ...(pricingAuditMeta || {}),
        productType:
          normalizePricingAuditProductType(pricingAuditMeta?.productType) ??
          PRICING_AUDIT_PRODUCT_TYPE.HOTEL,
        pricingModel:
          pricingAuditMeta?.pricingModel != null &&
          String(pricingAuditMeta.pricingModel).trim() !== ""
            ? String(pricingAuditMeta.pricingModel)
            : sourceType,
      }),
      input: { sourceType, rawData: safeRaw, entitlementsStatus },
      result: pending,
    });
    return pending;
  }

  let resolved = resolveAgentHotelPricing(normalized);

  if (isHotelSearchEntitlementsFailed(entitlementsStatus)) {
    resolved = {
      ...resolved,
      reason: resolved.reason ?? ENTITLEMENTS_FAILED_FALLBACK_ZERO,
      entitlementsTraceNote: ENTITLEMENTS_FAILED_FALLBACK_ZERO,
      auditSteps: [
        ...(Array.isArray(resolved.auditSteps) ? resolved.auditSteps : []),
        `agent_hotel:${ENTITLEMENTS_FAILED_FALLBACK_ZERO}`,
      ],
    };
  }

  const auditPricingModel =
    pricingAuditMeta &&
    typeof pricingAuditMeta === "object" &&
    pricingAuditMeta.pricingModel != null &&
    String(pricingAuditMeta.pricingModel).trim() !== ""
      ? String(pricingAuditMeta.pricingModel)
      : sourceType;

  recordPricingAudit({
    kind: "hotel",
    meta: compactPricingAuditMeta({
      ...(pricingAuditMeta || {}),
      productType:
        normalizePricingAuditProductType(pricingAuditMeta?.productType) ??
        PRICING_AUDIT_PRODUCT_TYPE.HOTEL,
      pricingModel: auditPricingModel,
    }),
    input: { sourceType, rawData: safeRaw },
    result: resolved,
  });

  return resolved;
}

/**
 * Single display formatter for resolved agent-hotel pricing (symbol + amount).
 * @param {ReturnType<typeof resolveAgentHotelPricing>} resolved
 * @returns {string|null}
 */
export function formatResolvedAgentHotelPriceLine(resolved) {
  if (!resolved || resolved.pricingStatus !== "ok" || resolved.finalPrice == null) {
    return null;
  }
  return (
    formatHotelSearchAmountForDisplay(resolved.finalPrice, resolved.displayCurrency) ??
    String(resolved.finalPrice)
  );
}
